/**
 * Breakpoint Tokens
 *
 * Responsive breakpoint system
 * Matches Tailwind's default breakpoints
 */

// ============================================================================
// BREAKPOINT DEFINITIONS
// ============================================================================

/**
 * Breakpoint values in pixels
 */
export const BREAKPOINTS = {
  /** Small devices - 640px */
  sm: 640,

  /** Medium devices - 768px */
  md: 768,

  /** Large devices - 1024px */
  lg: 1024,

  /** Extra large devices - 1280px */
  xl: 1280,

  /** 2XL devices - 1536px */
  '2xl': 1536,
} as const;

/**
 * Breakpoint names
 */
export type Breakpoint = keyof typeof BREAKPOINTS;

// ============================================================================
// MEDIA QUERIES
// ============================================================================

/**
 * Generate media query string for min-width
 */
export const mediaQueryUp = (breakpoint: Breakpoint): string => {
  return `@media (min-width: ${BREAKPOINTS[breakpoint]}px)`;
};

/**
 * Generate media query string for max-width
 */
export const mediaQueryDown = (breakpoint: Breakpoint): string => {
  return `@media (max-width: ${BREAKPOINTS[breakpoint] - 1}px)`;
};

/**
 * Generate media query string for range
 */
export const mediaQueryBetween = (min: Breakpoint, max: Breakpoint): string => {
  return `@media (min-width: ${BREAKPOINTS[min]}px) and (max-width: ${BREAKPOINTS[max] - 1}px)`;
};

/**
 * Media query presets
 */
export const MEDIA_QUERIES = {
  /** Mobile first - Small and up */
  smUp: mediaQueryUp('sm'),

  /** Tablet and up */
  mdUp: mediaQueryUp('md'),

  /** Desktop and up */
  lgUp: mediaQueryUp('lg'),

  /** Large desktop and up */
  xlUp: mediaQueryUp('xl'),

  /** Extra large desktop and up */
  '2xlUp': mediaQueryUp('2xl'),

  /** Mobile only */
  smDown: mediaQueryDown('sm'),

  /** Tablet only */
  mdDown: mediaQueryDown('md'),

  /** Desktop only */
  lgDown: mediaQueryDown('lg'),

  /** Large desktop only */
  xlDown: mediaQueryDown('xl'),

  /** Mobile to tablet */
  smToMd: mediaQueryBetween('sm', 'md'),

  /** Tablet to desktop */
  mdToLg: mediaQueryBetween('md', 'lg'),

  /** Desktop to large desktop */
  lgToXl: mediaQueryBetween('lg', 'xl'),
} as const;

// ============================================================================
// DEVICE CATEGORIES
// ============================================================================

/**
 * Device category definitions
 */
export const DEVICE_CATEGORIES = {
  mobile: {
    min: 0,
    max: BREAKPOINTS.sm - 1,
  },
  tablet: {
    min: BREAKPOINTS.sm,
    max: BREAKPOINTS.lg - 1,
  },
  desktop: {
    min: BREAKPOINTS.lg,
    max: Infinity,
  },
} as const;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Check if current viewport matches breakpoint
 */
export const isBreakpointUp = (breakpoint: Breakpoint): boolean => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth >= BREAKPOINTS[breakpoint];
};

/**
 * Check if current viewport is below breakpoint
 */
export const isBreakpointDown = (breakpoint: Breakpoint): boolean => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < BREAKPOINTS[breakpoint];
};

/**
 * Get current breakpoint
 */
export const getCurrentBreakpoint = (): Breakpoint | 'xs' => {
  if (typeof window === 'undefined') return 'xs';

  const width = window.innerWidth;

  if (width >= BREAKPOINTS['2xl']) return '2xl';
  if (width >= BREAKPOINTS.xl) return 'xl';
  if (width >= BREAKPOINTS.lg) return 'lg';
  if (width >= BREAKPOINTS.md) return 'md';
  if (width >= BREAKPOINTS.sm) return 'sm';
  return 'xs';
};

/**
 * Check if mobile device
 */
export const isMobile = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < BREAKPOINTS.sm;
};

/**
 * Check if tablet device
 */
export const isTablet = (): boolean => {
  if (typeof window === 'undefined') return false;
  const width = window.innerWidth;
  return width >= BREAKPOINTS.sm && width < BREAKPOINTS.lg;
};

/**
 * Check if desktop device
 */
export const isDesktop = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth >= BREAKPOINTS.lg;
};
